import * as React from 'react';
import * as I18n from 'i18n-js';
import { get } from 'lodash';
import gql from 'graphql-tag';

import {
  core_apimessages_Listing_ListingType,
  core_apimessages_ShippingMethod,
  core_apimessages_ShippingPrice as ShippingPrice,
  core_apimessages_Money as Money,
  Item2PriceWithShippingData,
} from '../rql';

import PriceDisplay from './price_display';
import RibbonView from './ribbon_view';

import {
  withUserContext,
  IUserContext,
} from './user_context_provider';

interface IProps extends IUserContext {
  listing: Item2PriceWithShippingData.Fragment;
  inlineRibbon?: boolean;
}

export const Item2PriceWithShippingFragment = gql`
  fragment Item2PriceWithShippingData on Listing {
    state
    stateDescription
    taxIncluded
    taxIncludedHint
    listingType
    pricing {
      originalPrice {
        display
      }
      ribbon {
        display
      }
      buyerPrice {
        amountCents
        currency
        display
      }
    }
    shipping {
      freeExpeditedShipping
      localPickupOnly
      shippingPrices {
        shippingMethod
        rate {
          amountCents
          currency
          display
        }
      }
    }
  }
`;

export class PriceWithShipping extends React.Component<IProps, null> {
  get standardShippingRate(): Money {
    const shippingPrice = get(this.props, 'listing.shipping.shippingPrices', []).find((price: ShippingPrice) => {
      return price.shippingMethod === core_apimessages_ShippingMethod.SHIPPED;
    });
    return shippingPrice && shippingPrice.rate;
  }

  get expeditedShippingRate(): Money {
    const shippingPrice = get(this.props, 'listing.shipping.shippingPrices', []).find((price: ShippingPrice) => {
      return price.shippingMethod === core_apimessages_ShippingMethod.EXPEDITED_SHIPPING;
    });
    return shippingPrice && shippingPrice.rate;
  }

  get taxIncluded(): boolean {
    return get(this.props, 'listing.taxIncluded', false);
  }

  get taxIncludedHint(): string {
    return get(this.props, 'listing.taxIncludedHint', '');
  }

  get showIncludedTax(): boolean {
    return this.taxIncluded && this.taxIncludedHint !== '';
  }

  get isLive(): boolean {
    return get(this.props, 'listing.state', '') === 'live';
  }

  isAuction(): boolean {
    return this.props.listing.listingType === core_apimessages_Listing_ListingType.AUCTION;
  }

  isFreeExpeditedShipping(): boolean {
    if (!this.props.listing.shipping) {
      return false;
    }

    return this.props.listing.shipping.freeExpeditedShipping;
  }

  isLocalPickupOnly(): boolean {
    if (!this.props.listing.shipping) {
      return false;
    }

    return this.props.listing.shipping.localPickupOnly;
  }

  hasDiscount(): boolean {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText(): string {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (!this.hasDiscount()) {
      return null;
    }

    return (
      <div className="price-with-shipping__price__original">
        {this.props.listing.pricing.originalPrice.display}
      </div>
    );
  }

  renderPrice() {
    return (
      <PriceDisplay
        amountCents={this.props.listing.pricing.buyerPrice.amountCents}
        currency={this.props.listing.pricing.buyerPrice.currency}
        display={this.props.listing.pricing.buyerPrice.display}
        auction={this.isAuction()}
      />
    );
  }

  renderShippingText() {
    if (this.isFreeExpeditedShipping()) {
      return (
        <div className="price-with-shipping__shipping price-with-shipping__shipping--free-expedited" data-tooltip-text={I18n.t('commons.freeExpeditedShippingInfo')} data-tooltip-align="left">
          <span className="icon-r-question-circle">
            {I18n.t('commons.freeExpeditedShipping')}
          </span>
        </div>
      );
    }

    if (this.isLocalPickupOnly()) {
      return (
        <div className="price-with-shipping__shipping">
          {I18n.t('commons.localPickupOnly')}
        </div>
      );
    }

    if (this.standardShippingRate && this.expeditedShippingRate) {
      const freeAndExpeditedShipping = this.standardShippingRate.amountCents === 0 && this.expeditedShippingRate.amountCents > 0;

      if (freeAndExpeditedShipping) {
        return (
          <>
            <div className="price-with-shipping__shipping">
              {I18n.t('commons.freeShipping')}
            </div>
            <div className="price-with-shipping__shipping price-with-shipping__shipping--expedited">
              {I18n.t('commons.expeditedShippingAvailable')}
            </div>
          </>
        );
      }
    }

    if (this.standardShippingRate) {
      const freeShipping = this.standardShippingRate.amountCents === 0;

      if (freeShipping) {
        return (
          <div className="price-with-shipping__shipping">
            {I18n.t('commons.freeShipping')}
          </div>
        );
      }

      return (
        <div className="price-with-shipping__shipping">
          +
          <span className="mlr-space">
            <PriceDisplay
              amountCents={this.standardShippingRate.amountCents}
              currency={this.standardShippingRate.currency}
              display={this.standardShippingRate.display}
            />
          </span>
          {I18n.t('commons.shipping')}
        </div>
      );
    }

    return (
      <div className="price-with-shipping__shipping">
        {I18n.t('commons.plusShipping')}
      </div>
    );
  }

  renderIncludedTax() {
    if (!this.showIncludedTax) {
      return null;
    }

    return (
      <div className="price-with-shipping__included-tax">
        {this.taxIncludedHint}
      </div>
    );
  }

  renderAcceptsOffers() {
    if (this.props.listing.listingType !== core_apimessages_Listing_ListingType.MAKE_AN_OFFER) {
      return null;
    }

    return (
      <div className="price-with-shipping__accepts-offers">
        {I18n.t('commons.acceptsOffers')}
      </div>
    );
  }

  renderRibbon() {
    if (!this.isLive) { return; }
    return (
      <div className="price-with-shipping__discount">
        <RibbonView
          auction={this.isAuction()}
          listingStateDescription={this.props.listing.stateDescription}
          listingStateSlug={this.props.listing.state}
          discountDisplay={this.ribbonText()}
          unstyled
        />
      </div>
    );
  }

  render() {
    return (
      <div className="price-with-shipping">
        <div className="price-with-shipping__price">
          {this.isLive && this.renderOriginalPrice()}
          <div className="price-with-shipping__price__amount">
            {this.renderPrice()}
            {this.props.inlineRibbon && this.renderRibbon()}
            {this.renderIncludedTax()}
          </div>
        </div>
        {this.renderShippingText()}
        {!this.props.inlineRibbon && this.renderRibbon()}
      </div>
    );
  }
}

export default (withUserContext(PriceWithShipping));



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/price_with_shipping.tsx